import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MessageCircle, Send, Trash2, Loader2 } from 'lucide-react';

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  } catch { return ''; }
};

/**
 * Estimate Q&A panel — Adobe-style threaded comments between customer and contractor.
 *
 * Modes:
 *   - "public" (customer): uses /api/public/estimate/{token}/comments
 *   - "contractor": uses /api/organizations/{orgId}/estimates/{estimateId}/comments
 */
export default function EstimateQA({
  mode,
  token,           // public token (required for public mode)
  orgId,           // required for contractor mode
  estimateId,      // required for contractor mode
  defaultAuthorName = '',
  defaultAuthorEmail = '',
  primaryColor = '#1e3a8a',
  contractorName = '',
  contractorAvatar = null,
  customerName = '',
  user,            // contractor user object
  className = '',
}) {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [body, setBody] = useState('');
  const [authorName, setAuthorName] = useState(defaultAuthorName);
  const [authorEmail, setAuthorEmail] = useState(defaultAuthorEmail);

  const listUrl = mode === 'public'
    ? `${apiBase}/api/public/estimate/${token}/comments`
    : `${apiBase}/api/organizations/${orgId}/estimates/${estimateId}/comments`;

  const postUrl = mode === 'public'
    ? `${apiBase}/api/public/estimate/${token}/comments`
    : `${apiBase}/api/organizations/${orgId}/estimates/${estimateId}/comments?user_id=${encodeURIComponent(user?.id || '')}&user_name=${encodeURIComponent(user?.name || '')}`;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(listUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setComments(data.comments || []);
    } catch (e) {
      console.error('load comments failed', e);
    } finally {
      setLoading(false);
    }
  }, [listUrl]);

  useEffect(() => { load(); }, [load]);

  // Light auto-refresh every 8s while panel mounted (feels like real chat)
  useEffect(() => {
    const t = setInterval(() => load(), 8000);
    return () => clearInterval(t);
  }, [load]);

  const handleSubmit = async () => {
    if (!body.trim()) return;
    if (mode === 'public') {
      if (!authorName.trim()) {
        toast.error('Please enter your name');
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        body: body.trim(),
        author_name: authorName.trim(),
        author_email: authorEmail.trim(),
      };
      const res = await fetch(postUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setBody('');
      await load();
      toast.success(mode === 'public' ? 'Question sent — the contractor will be notified.' : 'Reply posted');
    } catch (e) {
      console.error(e);
      toast.error('Could not post — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (mode !== 'contractor') return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      const res = await fetch(
        `${apiBase}/api/organizations/${orgId}/estimates/${estimateId}/comments/${commentId}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      console.error(e);
      toast.error('Could not delete');
    }
  };

  return (
    <div
      className={`rounded-2xl border bg-white shadow-sm ${className}`}
      data-testid="estimate-qa-panel"
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-5 py-4 border-b rounded-t-2xl"
        style={{ background: `${primaryColor}0d` }}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: `${primaryColor}1f`, color: primaryColor }}
        >
          <MessageCircle className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-base flex items-center gap-2" style={{ color: primaryColor }}>
            Conversation
            {comments.length > 0 && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-white/70" style={{ color: primaryColor }}>
                {comments.length}
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {mode === 'public'
              ? 'Ask anything about this estimate — replies appear here, and we\'ll email you when there\'s a new one.'
              : 'Replies post here AND email the customer with a link back to this estimate.'}
          </p>
        </div>
        {comments.length > 0 && (
          <span className="text-[10px] text-gray-400 hidden sm:inline" data-testid="qa-last-reply">
            Last: {formatTime(comments[comments.length - 1].created_at)}
          </span>
        )}
      </div>

      {/* Thread */}
      <div className="p-5 space-y-3 max-h-[420px] overflow-y-auto" data-testid="qa-thread">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500">
            No questions yet. {mode === 'public' ? 'Ask the first one below 👇' : 'Customers can post questions from the public link.'}
          </div>
        ) : (
          comments.map(c => {
            const isCustomer = c.author_role === 'customer';
            return (
              <div
                key={c.id}
                className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}
                data-testid={`qa-comment-${c.id}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    isCustomer
                      ? 'bg-gray-100 text-gray-900 rounded-bl-sm'
                      : 'text-white rounded-br-sm'
                  }`}
                  style={!isCustomer ? { background: primaryColor } : {}}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
                      {c.author_name || (isCustomer ? 'Customer' : 'Contractor')}
                    </span>
                    <span className="text-[10px] opacity-60">{formatTime(c.created_at)}</span>
                    {mode === 'contractor' && (
                      <button
                        onClick={() => handleDelete(c.id)}
                        className="ml-auto opacity-50 hover:opacity-100"
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{c.body}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      <div className="border-t p-4 space-y-2.5 bg-gray-50/50 rounded-b-2xl">
        {mode === 'public' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label htmlFor="qa-name" className="text-xs">Your name</Label>
              <Input
                id="qa-name"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-1 h-9 text-sm"
                data-testid="qa-author-name"
              />
            </div>
            <div>
              <Label htmlFor="qa-email" className="text-xs">Email <span className="text-gray-400">(optional)</span></Label>
              <Input
                id="qa-email"
                type="email"
                value={authorEmail}
                onChange={(e) => setAuthorEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 h-9 text-sm"
                data-testid="qa-author-email"
              />
            </div>
          </div>
        )}
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={mode === 'public'
            ? 'Ask about timeline, materials, change orders…'
            : 'Reply to the customer…'}
          rows={2}
          className="text-sm resize-none"
          data-testid="qa-composer"
        />
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-gray-500">
            {mode === 'public' ? 'You\'ll get an email when they reply.' : 'The customer will be emailed your reply.'}
          </p>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !body.trim() || (mode === 'public' && !authorName.trim())}
            className="h-9"
            style={{ background: primaryColor }}
            data-testid="qa-submit"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-1.5" />
            )}
            {mode === 'public' ? 'Send question' : 'Send reply'}
          </Button>
        </div>
      </div>
    </div>
  );
}
